import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";
import { ArticleDTO, KitHistoryEntry } from "../../models/ArticleDTO";

const BASE = "/admin/campus/";

/** Kits / productos (Sep 2026): bitácora, entregas, anulación y reactivación. */
export class KitsService {

	private clean(filters: Record<string, any>): Record<string, any> {
		const params: Record<string, any> = {};
		Object.keys(filters || {}).forEach((key) => { const value = filters[key]; if (value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && value.length < 1)) params[key] = value; });
		return params;
	}

	/** Lists. tab: paid_undelivered | partially_paid | undelivered | delivered | cancelled | pending_unpaid | sold | all. section: kits | kitSales. */
	list = async (campusId: number, section: "kits" | "kitSales", tab: string, filters: Record<string, any>, page: number, perPage: number): Promise<AxiosResponse<any>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}`, { params: { ...this.clean(filters), tab, page, perPage } });
	}

	salesTotals = async (campusId: number, section: "kits" | "kitSales", filters: Record<string, any>): Promise<AxiosResponse<{ kits: number; students: number; sold: number; paid: number; pending: number }>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}/_salesTotals`, { params: this.clean(filters) });
	}

	/** Reporte de Ventas · Inscritos Recientes. */
	recentEnrollees = async (campusId: number, section: "kits" | "kitSales", filters: Record<string, any>, page: number, perPage: number): Promise<AxiosResponse<any>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}/_recentEnrollees`, { params: { ...this.clean(filters), page, perPage } });
	}

	/** Paid / partially paid kits not delivered, grouped by product with stock and shortage. */
	withoutInventory = async (campusId: number, section: "kits" | "kitSales", filters: Record<string, any>): Promise<AxiosResponse<any[]>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}/_withoutInventory`, { params: this.clean(filters) });
	}

	studentsWithoutProducts = async (campusId: number, section: "kits" | "kitSales", filters: Record<string, any>, page: number, perPage: number): Promise<AxiosResponse<any>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}/_studentsWithoutProducts`, { params: { ...this.clean(filters), page, perPage } });
	}

	soldByAdvisor = async (campusId: number, section: "kits" | "kitSales", filters: Record<string, any>): Promise<AxiosResponse<any[]>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}/_soldByAdvisor`, { params: this.clean(filters) });
	}

	/** Courses, periods, schedules, mentors, advisors, articles, weekdays of the campus for the filter bars. */
	filterOptions = async (campusId: number, section: "kits" | "kitSales"): Promise<AxiosResponse<any>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}/_filters`);
	}

	/** Excel file (blob). kind: kits | students | advisors | inventory. */
	report = async (campusId: number, section: "kits" | "kitSales", kind: string, tab: string, filters: Record<string, any>): Promise<AxiosResponse<Blob>> => {
		return axiosInstance.get(`${BASE}${campusId}/${section}/_report`, { params: { ...this.clean(filters), kind, tab }, responseType: "blob" });
	}

	/** Free note on a kit (history only). */
	addNote = async (campusId: number, kitId: number, note: string): Promise<AxiosResponse<{ message: string; kit: ArticleDTO }>> => {
		return axiosInstance.post(`${BASE}${campusId}/kits/${kitId}/_note`, { note });
	}

	/** Sellers eligible for kits/products in the campus (kits team per country). Section "store". */
	/** Pago digital reportado por Ventas: comprobante → ticket en Pagos por Procesar. Multipart (archivos). */
	reportPayment = async (campusId: number, kitIds: number[], data: { typeId: number; amount: number | null; reference: string; paidAt: string; note: string }, files: File[]): Promise<AxiosResponse<{ message: string; intentId: number }>> => {
		const body = new FormData();
		body.append("kitIds", JSON.stringify(kitIds));
		body.append("typeId", String(data.typeId));
		if (data.amount !== null && data.amount !== undefined) body.append("amount", String(data.amount));
		if (data.reference) body.append("reference", data.reference);
		if (data.paidAt) body.append("paidAt", data.paidAt);
		if (data.note) body.append("note", data.note);
		files.forEach((file) => body.append("files[]", file));
		return axiosInstance.post(`${BASE}${campusId}/store/_kitPayment`, body);
	};

	kitAdvisorCandidates = async (campusId: number): Promise<AxiosResponse<{ id: string; name: string; email: string | null; roles: string[] }[]>> => {
		return axiosInstance.get(`${BASE}${campusId}/store/_kitAdvisorCandidates`);
	}

	/** Bitácora of one kit, oldest first. */
	getHistory = async (campusId: number, kitId: number): Promise<AxiosResponse<KitHistoryEntry[]>> => {
		return axiosInstance.get<KitHistoryEntry[]>(`${BASE}${campusId}/kits/${kitId}/_history`);
	}

	/** Bitácora of every kit of a student in the campus, newest first. */
	getStudentHistory = async (campusId: number, studentId: number): Promise<AxiosResponse<KitHistoryEntry[]>> => {
		return axiosInstance.get<KitHistoryEntry[]>(`${BASE}${campusId}/kits/students/${studentId}/_history`);
	}

	/** Partial or total delivery with a mandatory free-text note (section "kits"). */
	registerDelivery = async (campusId: number, kitId: number, type: "partial" | "total", note: string): Promise<AxiosResponse<{ message: string; kit: ArticleDTO }>> => {
		return axiosInstance.post(`${BASE}${campusId}/kits/${kitId}/_delivery`, { type, note });
	}

	/** Fix the delivery state directly, e.g. "Desconocido" → real state (section "kits"). */
	setDeliveryStatus = async (campusId: number, kitId: number, deliveryStatusId: number, note: string): Promise<AxiosResponse<{ message: string; kit: ArticleDTO }>> => {
		return axiosInstance.put(`${BASE}${campusId}/kits/${kitId}/_deliveryStatus`, { deliveryStatusId, note });
	}

	/** Anular a $0-paid kit with a mandatory reason (section "kitSales"). */
	cancel = async (campusId: number, kitId: number, reason: string): Promise<AxiosResponse<{ message: string; kit: ArticleDTO }>> => {
		return axiosInstance.post(`${BASE}${campusId}/kitSales/${kitId}/_cancel`, { reason });
	}

	/** Bring an "Anulado" kit back to "Pendiente" with a mandatory reason (section "kitSales"). */
	reactivate = async (campusId: number, kitId: number, reason: string): Promise<AxiosResponse<{ message: string; kit: ArticleDTO }>> => {
		return axiosInstance.post(`${BASE}${campusId}/kitSales/${kitId}/_reactivate`, { reason });
	}
}
